/**
 * The taskbar well's geometry — the sizes the layout, the drag's slot
 * arithmetic and the scroll fade all depend on. Change one, change the others:
 * `gap-2` in TaskBar's well IS `RAIL_GAP`, `py-0.5` IS `RAIL_PAD`.
 */
export const RAIL_TILE = 40
export const RAIL_GAP = 8
export const RAIL_SLOT = RAIL_TILE + RAIL_GAP
export const RAIL_PAD = 2   // py-0.5

/** A well shorter than this many rows shows no chevrons: two of them around one row is clutter, and the fade still marks it. */
export const RAIL_CHEVRON_MIN_ROWS = 2

/** The painted glyph inside a 40px tile: a mini-app's squircle, and a standalone HTML app's globe. */
export const RAIL_APP_GLYPH = 29
export const RAIL_HTML_GLYPH = 20

/**
 * THE SCROLL FADE — how far into the well each end fades.
 *
 * It has to reach PAINT, not just the tile's box. A glyph is centred in its tile
 * and tiles sit a gap apart, so between two glyphs runs a band with nothing in it
 * — 15px between two folders, 19px between two mini-apps, 28px between two
 * globes. The fade used to be 14px: whenever the well's edge landed in one of
 * those bands (about one window height in five, repeating every 48px) it dimmed
 * nothing, the last visible icon read as the end of the list, and nobody could
 * tell the rail scrolled.
 *
 * What makes a fade READ is a pixel that is both still visible and visibly
 * dimmed — an icon at 5% opacity is dimmed but not seen. The test helper
 * `railFadeWorstCue` (`__tests__/rail-fade-measure.ts`) measures exactly that at
 * the worst edge position, and the curve and length were chosen on it: a LINEAR ramp beats every eased one tried (t², t^1.5,
 * smoothstep, √t), because its half-opacity point sits mid-band where an empty
 * run cannot hide it from both sides. 40px — under one row — gives a mini-app a
 * worst cue of 0.26 and a folder 0.31 (the old 14px: 0 for both).
 *
 * No fade shorter than a row can do the same for two adjacent globes (0.15): a
 * 28px empty run needs a 56px fade to keep a half-dimmed pixel in view. The
 * chevrons (`.ws-rail-more`) are the cue that holds at every height and in every
 * theme; the fade is the ambient one.
 *
 * Each end is capped at `RAIL_FADE_MAX_FRACTION` of the well, so a very short
 * window, with both ends fading, does not fade the whole list.
 */
export const RAIL_FADE_PX = 40
export const RAIL_FADE_MAX_FRACTION = 0.3

/** The fade length the mask paints for a well `wellHeight` px tall — `min(len, cap)` in `index.css`. */
export function railFadeLength(wellHeight: number): number {
  return Math.min(RAIL_FADE_PX, RAIL_FADE_MAX_FRACTION * wellHeight)
}
